import { FC, useState } from 'react';
import { Controller } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import { Autocomplete as MuiAutocomplete } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from 'assets/iconComponents/SearchIcon';
import { DeleteIcon } from 'assets';
import { palette } from 'view/themes/palette';
import * as S from './styled';

const DEFAULT_MAX_SELECTION = 15;
const EMPTY_VALUE: never[] = [];

type SelectOption = { id: number; name: string; group?: string };

interface SearchableMultiplySelectProps {
  name: string;
  label: string;
  options: SelectOption[];
  control: any;
  rules?: object;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  maxSelection?: number;
}

const SearchableMultiplySelect: FC<SearchableMultiplySelectProps> = ({
  name,
  label,
  options = [],
  control,
  rules = {},
  error = '',
  disabled = false,
  placeholder = 'Search...',
  maxSelection = DEFAULT_MAX_SELECTION,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const selected: SelectOption[] = field.value ?? EMPTY_VALUE;
        const isMaxSelected = selected.length >= maxSelection;

        return (
          <S.Wrapper disabled={disabled}>
            <S.SelectLabel error={error}>{label}</S.SelectLabel>
            <MuiAutocomplete<SelectOption, true, true, false>
              multiple
              disabled={disabled}
              open={open}
              onOpen={() => {
                setOpen(true);
              }}
              onClose={() => {
                setOpen(false);
              }}
              options={options}
              value={selected}
              inputValue={inputValue}
              onInputChange={(_, next) => {
                setInputValue(next);
              }}
              onChange={(_, next) => {
                if (next.length <= maxSelection) {
                  field.onChange(next);
                }
              }}
              getOptionLabel={(option) => {
                return option.name || '';
              }}
              getOptionKey={(option) => {
                return option.id;
              }}
              isOptionEqualToValue={(option, value) => {
                return option.id === value.id;
              }}
              getOptionDisabled={(option) => {
                return (
                  isMaxSelected &&
                  !selected.some((v) => {
                    return v.id === option.id;
                  })
                );
              }}
              renderTags={() => {
                return null;
              }}
              disableClearable
              disableCloseOnSelect
              renderOption={(props, option, { selected: isSelected }) => {
                const { key, ...rest } = props as typeof props & { key: string };
                return (
                  <S.AutocompleteOption {...rest} key={key} isSelected={isSelected}>
                    <span>{option.name}</span>
                    {isSelected && (
                      <S.Checkmark>
                        <CheckIcon />
                      </S.Checkmark>
                    )}
                  </S.AutocompleteOption>
                );
              }}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    placeholder={placeholder}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchIcon />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '.MuiInputBase-root': {
                        minHeight: 40,
                        color: palette.text.body,
                        borderRadius: '8px',
                        padding: '8px 12px',

                        '.MuiOutlinedInput-notchedOutline': {
                          borderColor: error ? palette.alerts.error : palette.border.default,
                          borderRadius: '8px',
                        },

                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: error ? palette.alerts.error : palette.hover.brand,
                        },

                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px',
                          borderColor: palette.primary.purple500,
                        },
                      },
                    }}
                  />
                );
              }}
              sx={{ width: '100%' }}
              popupIcon={<KeyboardArrowDownIcon />}
              slotProps={{
                popupIndicator: {
                  sx: {
                    color: palette.unknown.icon_grey,
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  },
                },
                popper: { sx: { borderRadius: '8px' } },
                paper: {
                  sx: {
                    marginTop: '2px',
                    borderRadius: '8px',
                    boxShadow:
                      '0px 20px 24px -4px rgba(0, 0, 0, 0.08), 0px 8px 8px -4px rgba(0, 0, 0, 0.03)',
                    '.MuiAutocomplete-noOptions': { padding: '8px 12px' },
                  },
                },
                listbox: {
                  sx: {
                    border: 'none',
                    borderRadius: '8px',
                    maxHeight: 245,
                    overflowY: 'auto',
                    padding: 0,
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#D8DBE8',
                      borderRadius: '10px',
                      transition: 'background 0.2s ease-in-out',
                    },
                    '&::-webkit-scrollbar-thumb:hover': { background: '#BFC3D4' },
                    '&::-webkit-scrollbar-thumb:active': { background: '#A5AABF' },
                  },
                },
              }}
            />

            {selected.length > 0 && (
              <S.SelectedOptionsContainer>
                {selected.map((option) => {
                  return (
                    <S.SelectedOption
                      key={option.id}
                      label={option.name}
                      deleteIcon={
                        <S.DeleteIcon>
                          <DeleteIcon />
                        </S.DeleteIcon>
                      }
                      onDelete={() => {
                        field.onChange(
                          selected.filter((val) => {
                            return val.id !== option.id;
                          })
                        );
                      }}
                    />
                  );
                })}
              </S.SelectedOptionsContainer>
            )}
            {error && <S.ErrorMessage>{error}</S.ErrorMessage>}
          </S.Wrapper>
        );
      }}
    />
  );
};

export default SearchableMultiplySelect;
